import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldAlert, Briefcase, ClipboardCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import ThemeToggle from "./ThemeToggle";
import UserMenu from "./UserMenu";
import BrandLogo from "./BrandLogo";
import NotificationsBell from "./NotificationsBell";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { cn } from "../lib/utils";

function canViewSecurity(user) {
  if (!user) return false;
  if (user.role === "Super Admin") return true;
  const scopes = user.scopes || [];
  return scopes.includes("administrator") || scopes.includes("security");
}

function canViewAuditorPortal(user) {
  if (!user) return false;
  if (user.role === "Super Admin" || user.role === "Auditor") return true;
  const scopes = user.scopes || [];
  return scopes.includes("administrator");
}

export default function Header({ user }) {
  const { settings } = useSettings();
  const { user: authUser } = useAuth();
  const effectiveUser = user || authUser;
  const versionLabel = settings?.version_label || "v1.0";
  const showSecurity = canViewSecurity(effectiveUser);
  const showAuditorPortal = canViewAuditorPortal(effectiveUser);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!showSecurity) return;
    let cancelled = false;
    api.get("/security/summary")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showSecurity]);

  const openCount = (summary?.open_critical || 0) + (summary?.open_high || 0);

  return (
    <div className="w-full bg-background border-b border-border">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 h-[88px] md:h-[232px] flex items-center gap-4">
        {/* Logo — full wordmark artwork (includes brand text). 3x size on
            desktop per brand request; kept at the original compact size on
            mobile so it doesn't crowd out search/notifications/user menu. */}
        <div className="flex items-center gap-2 min-w-fit" data-testid="header-brand">
          <BrandLogo variant="full" className="h-[72px] md:h-[216px] w-auto" />
        </div>

        {/* Version pill (right of logo) — configurable in Settings > Super Admin */}
        <div className="hidden md:inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2.5 py-1 border border-primary/20">
          {versionLabel}
        </div>

        {/* Search */}
        <div className="flex-1 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users, orgs, apps…"
              className="pl-9 h-10 bg-muted/40 border-border focus-visible:ring-primary/40"
            />
          </div>
        </div>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2">
          <NotificationsBell />
          <ThemeToggle />
          <Button asChild variant="outline" className="hidden md:inline-flex gap-1.5 h-10 rounded-lg" data-testid="header-employee-portal-btn">
            <Link to="/portal">
              <Briefcase className="w-4 h-4" />
              <span className="text-[13px] font-medium">Employee Portal</span>
            </Link>
          </Button>
          {showAuditorPortal && (
            <Button asChild variant="outline" className="hidden md:inline-flex gap-1.5 h-10 rounded-lg" data-testid="header-auditor-portal-btn">
              <Link to="/auditor">
                <ClipboardCheck className="w-4 h-4" />
                <span className="text-[13px] font-medium">Auditor Portal</span>
              </Link>
            </Button>
          )}
          {showSecurity && (
            <Button
              asChild
              variant="outline"
              className={cn(
                "hidden md:inline-flex gap-1.5 h-10 rounded-lg",
                openCount > 0
                  ? "border-amber-400/50 text-amber-700 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-500/10 hover:bg-amber-100/60 dark:hover:bg-amber-500/15"
                  : "border-emerald-400/50 text-emerald-700 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-500/10 hover:bg-emerald-100/60 dark:hover:bg-emerald-500/15",
              )}
              data-testid="header-security-btn"
            >
              <Link to="/analytics/security">
                <ShieldAlert className="w-4 h-4" />
                <span className="text-[13px] font-medium">Security</span>
                {openCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full bg-rose-500 text-white text-[10.5px] font-bold min-w-[18px] h-[18px] px-1.5" data-testid="header-security-count">
                    {openCount}
                  </span>
                )}
              </Link>
            </Button>
          )}
          <UserMenu user={effectiveUser} />
        </div>
      </div>
    </div>
  );
}
